import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { isPracticalType } from '../../utils/practicalResults';
import { ACCENT_ACADEMIC } from '../../lib/personAccents';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR } from '../graph/graphConfig';
import type { ProjectPerson, SimilarProject } from '../../types/api';

interface SimilarResultsTableProps {
  results: SimilarProject[];
  parentPeopleIds?: Set<number>;
}

const PAGE_SIZE = 10;

export default function SimilarResultsTable({
  results,
  parentPeopleIds,
}: SimilarResultsTableProps) {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setExpanded(new Set());
  }, [results]);

  const visibleResults = results.slice(0, visibleCount);
  const canFetchMore = visibleCount < results.length;

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleNav = (path: string, e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    navigate(path);
  };

  if (!results.length) {
    return (
      <p className="m-0 text-sm text-ctu-text-muted italic">{t('similar.empty')}</p>
    );
  }

  return (
    <div className="space-y-4">
      <ul className="divide-y divide-ctu-border overflow-hidden rounded-md border border-ctu-border">
        {visibleResults.map((result, index) => {
          const isOpen = expanded.has(result.id);
          const practical = result.type ? isPracticalType(result.type) : false;
          const typeBadgeStyle = result.type
            ? practical
              ? undefined
              : { backgroundColor: ACCENT_ACADEMIC, color: '#ffffff', borderColor: 'transparent' }
            : undefined;
          const typeBadgeClass = practical
            ? 'bg-primary text-primary-foreground border-transparent font-normal hover:bg-primary'
            : 'border-transparent font-normal text-white';

          const sharedPerson = parentPeopleIds
            ? result.people.find((p) => parentPeopleIds.has(p.id))
            : undefined;

          return (
            <li
              key={result.id}
              className={`group transition-colors ${isOpen ? 'bg-muted/30' : 'hover:bg-muted/20'}`}
            >
              <div
                className="flex cursor-pointer items-start gap-4 px-4 py-3.5"
                onClick={() => toggle(result.id)}
              >
                <span
                  className="mt-1 w-7 shrink-0 select-none font-mono text-[11px] tabular-nums tracking-tight text-ctu-text-muted"
                  aria-hidden
                >
                  {String(index + 1).padStart(2, '0')}
                </span>

                <div className="min-w-0 flex-1 space-y-2">
                  <a
                    href={`/outcome/${result.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNav(`/outcome/${result.id}`, e);
                    }}
                    className="block break-words text-sm font-medium leading-snug text-ctu-text decoration-2 underline-offset-2 hover:text-primary hover:underline"
                  >
                    {result.name}
                  </a>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-ctu-text-muted">
                    {result.type && (
                      <Badge
                        variant="outline"
                        className={typeBadgeClass}
                        style={typeBadgeStyle}
                      >
                        {result.type}
                      </Badge>
                    )}
                    {result.year != null && (
                      <span className="tabular-nums">{result.year}</span>
                    )}
                    {sharedPerson && (
                      <PersonChip
                        person={sharedPerson}
                        compact
                        onNav={handleNav}
                        stopRowClick
                      />
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 items-start gap-3">
                  <div
                    className="flex flex-col items-end gap-1 pt-0.5"
                    title={t('similar.similarity')}
                  >
                    <span className="text-xs font-medium tabular-nums text-ctu-text">
                      {result.similarity.toFixed(2)}
                    </span>
                    <ScoreBar value={result.similarity} />
                  </div>
                  <Button
                    asChild
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-ctu-text-muted hover:text-primary"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={t('similar.viewDetail')}
                    title={t('similar.viewDetail')}
                  >
                    <a
                      href={`/outcome/${result.id}`}
                      onClick={(e) => handleNav(`/outcome/${result.id}`, e)}
                    >
                      <ArrowUpRight className="h-4 w-4" />
                    </a>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-ctu-text-muted hover:text-ctu-text"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(result.id);
                    }}
                    aria-label={isOpen ? t('similar.collapse') : t('similar.expand')}
                    aria-expanded={isOpen}
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </Button>
                </div>
              </div>

              {isOpen && (
                <div className="space-y-3 border-t border-ctu-border/60 bg-muted/40 px-4 py-3 pl-[3.75rem]">
                  {result.people.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {result.people.map((person) => (
                        <PersonChip
                          key={person.id}
                          person={person}
                          onNav={handleNav}
                        />
                      ))}
                    </div>
                  )}
                  {result.description ? (
                    <p className="m-0 whitespace-pre-line text-sm leading-relaxed text-ctu-text">
                      {result.description}
                    </p>
                  ) : (
                    <p className="m-0 text-sm italic text-ctu-text-muted">
                      {t('similar.noDescription')}
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {canFetchMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setVisibleCount((c) => Math.min(c + PAGE_SIZE, results.length))
            }
          >
            {t('similar.fetchMore')}
          </Button>
        </div>
      )}
    </div>
  );
}

interface PersonChipProps {
  person: ProjectPerson;
  compact?: boolean;
  stopRowClick?: boolean;
  onNav: (path: string, e: React.MouseEvent) => void;
}

function PersonChip({ person, compact, stopRowClick, onNav }: PersonChipProps) {
  const abbr = person.faculty_abbr ?? '';
  const facultyColor = abbr ? FACULTY_COLORS[abbr] || DEFAULT_NODE_COLOR : null;
  const path = `/person/${person.id}`;

  return (
    <a
      href={path}
      onClick={(e) => {
        if (stopRowClick) e.stopPropagation();
        onNav(path, e);
      }}
      className={
        compact
          ? 'group inline-flex items-center gap-1.5 rounded-md border border-ctu-border bg-muted/30 px-2 py-0.5 text-xs font-medium text-ctu-text no-underline transition-colors hover:bg-primary hover:text-primary-foreground hover:border-primary'
          : 'group inline-flex items-center gap-2 rounded-md border border-ctu-border bg-muted/30 px-3 py-1.5 text-sm font-medium text-ctu-text no-underline transition-colors hover:bg-primary hover:text-primary-foreground hover:border-primary'
      }
    >
      {abbr && facultyColor && (
        <span
          className={
            compact
              ? 'inline-flex shrink-0 items-center rounded px-1 py-px text-[9px] font-semibold tracking-wide text-white'
              : 'inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-white'
          }
          style={{ backgroundColor: facultyColor }}
        >
          {abbr}
        </span>
      )}
      <span>{person.name}</span>
      {!compact && (
        <ArrowUpRight
          className="h-3.5 w-3.5 opacity-40 transition-opacity group-hover:opacity-100"
          aria-hidden
        />
      )}
    </a>
  );
}

function ScoreBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  const fillColor =
    value >= 0.7 ? 'bg-emerald-500' : value >= 0.5 ? 'bg-amber-500' : 'bg-slate-400';
  return (
    <div className="h-1.5 w-12 overflow-hidden rounded-full bg-muted">
      <div className={`h-full rounded-full ${fillColor}`} style={{ width: `${pct}%` }} />
    </div>
  );
}
